import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import Loader from "../../../components/Loader";
import Toast, { useToast } from "../../../components/Toast";
import { getUser } from "../../../config/auth";
import { canManageUsers } from "../../../config/rbac";
import FilterChip from "../../../components/FilterChip";
import Pagination, { usePagination } from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import PageHeader from "../../../components/PageHeader";

const PAGE_SIZE = 10;

const ROLES = ["Admin", "Director", "CXO", "Head", "Manager", "Staff"];

const ALL_ROLES = ["Staff", "Manager", "Head", "CXO", "Director", "Admin"];

// Rows = roles, Columns = permissions (matches image layout)
const ROLE_DISPLAY = [
  { role: "Admin",              label: "Admin" },
  { role: "Director",           label: "Director" },
  { role: "CXO",                label: "CXO / CEO / CTO / CFO" },
  { role: "Head",               label: "Department Heads" },
  { role: "Manager",            label: "Managers" },
  { role: "Staff",              label: "Staff" },
];

// colLabel = multi-line header text (array of lines), matches image column headers exactly
const ROLE_PERMISSIONS = [
  { label: "Create Tasks",             colLabel: ["Create", "Tasks"],             roles: ALL_ROLES },
  { label: "Create MOMs",              colLabel: ["Create", "MOMs"],              roles: ALL_ROLES },
  { label: "Manage Users",             colLabel: ["Manage", "Users"],             roles: ["Admin"] },
  { label: "View Audit Log",           colLabel: ["View", "Audit", "Log"],        roles: ALL_ROLES },
  { label: "Validate & Close Tasks",   colLabel: ["Validate", "& Close", "Tasks"],roles: ["Admin", "Director", "CXO", "Head", "Manager"] },
  { label: "Review Timeline Requests", colLabel: ["Review", "Timeline", "Requests"], roles: ["Admin", "Director", "CXO", "Head", "Manager"] },
  { label: "View All Tasks",           colLabel: ["View", "All", "Tasks"],        roles: ALL_ROLES },
  { label: "Bulk Upload Tasks",        colLabel: ["Bulk", "Upload", "Tasks"],     roles: ["Admin", "Director", "CXO", "Head", "Manager"] },
  { label: "Manage Settings",          colLabel: ["Manage", "Settings"],          roles: ["Admin"] },
];

function RolePermissionsReference() {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        marginBottom: 24,
        border: "1px solid var(--border, #e5e7eb)",
        borderRadius: 14,
        overflow: "hidden",
        background: "var(--surface, #fff)",
      }}
    >
      {/* Toggle header */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>🔐</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-1, #111)" }}>
              Role Permissions Reference
            </div>
            <div style={{ fontSize: 12, color: "var(--text-3, #6b7280)", marginTop: 1 }}>
              See what each role can do before adding a user
            </div>
          </div>
        </div>
        <span
          style={{
            fontSize: 12,
            color: "var(--text-3, #9ca3af)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            display: "inline-block",
          }}
        >
          ▼
        </span>
      </button>

      {/* Table */}
      {open && (
        <div style={{ overflowX: "auto", borderTop: "1px solid var(--border, #e5e7eb)" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: "var(--surface-2, #f9fafb)" }}>
                <th
                  style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "var(--text-2, #374151)",
                    borderBottom: "1px solid var(--border, #e5e7eb)",
                    borderRight: "1px solid var(--border, #e5e7eb)",
                    whiteSpace: "nowrap",
                    minWidth: 120,
                  }}
                >
                  Dept
                </th>
                {ROLE_PERMISSIONS.map((perm) => (
                  <th
                    key={perm.label}
                    style={{
                      padding: "8px 10px",
                      textAlign: "center",
                      fontWeight: 600,
                      color: "var(--text-2, #374151)",
                      borderBottom: "1px solid var(--border, #e5e7eb)",
                      borderRight: "1px solid var(--border, #e5e7eb)",
                      fontSize: 12,
                      lineHeight: 1.35,
                      minWidth: 64,
                    }}
                  >
                    {perm.colLabel.map((line, i) => (
                      <span key={i} style={{ display: "block" }}>{line}</span>
                    ))}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROLE_DISPLAY.map((rd, i) => (
                <tr
                  key={rd.role}
                  style={{ background: i % 2 === 0 ? "transparent" : "var(--surface-2, #f9fafb)" }}
                >
                  <td
                    style={{
                      padding: "10px 16px",
                      fontWeight: 600,
                      color: "var(--text-1, #111)",
                      borderBottom: "1px solid var(--border, #e5e7eb)",
                      borderRight: "1px solid var(--border, #e5e7eb)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {rd.label}
                  </td>
                  {ROLE_PERMISSIONS.map((perm) => (
                    <td
                      key={perm.label}
                      style={{
                        padding: "10px 8px",
                        textAlign: "center",
                        borderBottom: "1px solid var(--border, #e5e7eb)",
                        borderRight: "1px solid var(--border, #e5e7eb)",
                      }}
                    >
                      {perm.roles.includes(rd.role) ? (
                        <span style={{ color: "#16a34a", fontSize: 15, fontWeight: 700 }}>✓</span>
                      ) : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EditUserModal({ user, users, departments, onClose, onSaved, isAdmin }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [role, setRole] = useState(user.role || "Staff");
  // Cascade dept: if user is in a sub-dept, parentDeptId = parent, subDeptId = user's dept
  const [parentDeptId, setParentDeptId] = useState(
    user.parentDepartmentId ? String(user.parentDepartmentId) : (user.departmentId ? String(user.departmentId) : "")
  );
  const [subDeptId, setSubDeptId] = useState(
    user.parentDepartmentId ? String(user.departmentId || "") : ""
  );
  const [reportsToUserId, setReportsToUserId] = useState(
    user.reportsToUserId || "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);

  const managersForDropdown = users.filter(
    (u) =>
      u.isActive !== false &&
      ["Manager", "Head", "CXO", "Director", "Admin"].includes(u.role) &&
      u.id !== user.id,
  );

  async function save() {
    if (!name.trim()) return setError("Name is required");
    if (!email.trim()) return setError("Email is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Enter a valid email address");
    setSaving(true);
    setError("");
    try {
      const resolvedDeptId = subDeptId || parentDeptId || null;
      const res = await apiFetch(`/api/users/${user.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          role,
          departmentId: resolvedDeptId,
          reportsToUserId: reportsToUserId || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      onSaved();
    } catch (e) {
      setError(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function resetPassword() {
    if (!newPassword.trim()) return setPwError("Enter a new password");
    if (newPassword.length < 6) return setPwError("Password must be at least 6 characters");
    setPwSaving(true);
    setPwError("");
    setPwSuccess(false);
    try {
      const res = await apiFetch(`/api/users/${user.id}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ newPassword }),
      });
      if (!res.ok) throw new Error(await res.text());
      setNewPassword("");
      setPwSuccess(true);
    } catch (e) {
      setPwError(e?.message || "Failed to reset password");
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="glass-card modal"
        style={{ maxWidth: 500 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Edit User — {user.name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && (
          <div
            style={{
              padding: "10px 12px",
              background: "var(--danger-soft)",
              border: "1px solid var(--danger-border)",
              borderRadius: 10,
              color: "var(--danger-text)",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "grid", gap: 14 }}>
          <FormField label="Name">
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label="Email">
            <input className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </FormField>
          <FormField label="Role">
            <select className="form-input" value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Department">
            <select
              className="form-input"
              value={parentDeptId}
              onChange={(e) => { setParentDeptId(e.target.value); setSubDeptId(""); }}
            >
              <option value="">No Department</option>
              {departments.filter(d => !d.parentDepartmentId).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </FormField>
          {parentDeptId && departments.some(d => String(d.parentDepartmentId) === String(parentDeptId)) && (
            <FormField label="Sub-department (optional)">
              <select
                className="form-input"
                value={subDeptId}
                onChange={(e) => setSubDeptId(e.target.value)}
              >
                <option value="">— None (stays in parent dept)</option>
                {departments.filter(d => String(d.parentDepartmentId) === String(parentDeptId)).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </FormField>
          )}
          <FormField label="Reporting Manager (optional)">
            <select
              className="form-input"
              value={reportsToUserId}
              onChange={(e) => setReportsToUserId(e.target.value)}
            >
              <option value="">None</option>
              {managersForDropdown.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.role})
                </option>
              ))}
            </select>
          </FormField>
        </div>

        {isAdmin && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-2)", marginBottom: 10 }}>
              Reset Password
            </div>
            {pwSuccess && (
              <div style={{ padding: "8px 12px", background: "var(--success-soft, #f0fdf4)", border: "1px solid var(--success-border, #bbf7d0)", borderRadius: 8, color: "#166534", fontSize: 13, marginBottom: 10 }}>
                Password reset successfully.
              </div>
            )}
            {pwError && (
              <div style={{ padding: "8px 12px", background: "var(--danger-soft)", border: "1px solid var(--danger-border)", borderRadius: 8, color: "var(--danger-text)", fontSize: 13, marginBottom: 10 }}>
                {pwError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="form-input"
                type="password"
                placeholder="New password (min 6 chars)"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPwError(""); setPwSuccess(false); }}
                style={{ flex: 1 }}
              />
              <button className="btn" onClick={resetPassword} disabled={pwSaving} style={{ whiteSpace: "nowrap" }}>
                {pwSaving ? "Saving..." : "Reset"}
              </button>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const me = getUser();
  const myRole = me?.role || "Staff";

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Staff");
  const [reportsToUserId, setReportsToUserId] = useState("");
  const [parentDeptId, setParentDeptId] = useState("");
  const [subDeptId, setSubDeptId] = useState("");

  const { toast, showToast, dismissToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [editingUser, setEditingUser] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [confirmStatusUser, setConfirmStatusUser] = useState(null); // user to deactivate/activate
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [subDeptFilter, setSubDeptFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState(""); // "active" | "inactive" | ""

  const topLevelDepts = useMemo(() => departments.filter(d => !d.parentDepartmentId), [departments]);
  const subDeptsForSelected = useMemo(
    () => deptFilter ? departments.filter(d => String(d.parentDepartmentId) === String(deptFilter)) : [],
    [departments, deptFilter]
  );

  const filteredUsers = useMemo(() => {
    let r = users;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    }
    if (roleFilter) r = r.filter((u) => u.role === roleFilter);
    if (deptFilter) {
      if (subDeptFilter) {
        // Sub-dept selected: show only users in that sub-dept
        r = r.filter((u) => String(u.departmentId) === String(subDeptFilter));
      } else {
        // Top-level dept selected: show users in that dept OR any of its sub-depts
        const subIds = departments
          .filter(d => String(d.parentDepartmentId) === String(deptFilter))
          .map(d => String(d.id));
        r = r.filter((u) => String(u.departmentId) === String(deptFilter) || subIds.includes(String(u.departmentId)));
      }
    }
    if (activeFilter === "active") r = r.filter((u) => u.isActive !== false);
    if (activeFilter === "inactive") r = r.filter((u) => u.isActive === false);
    return r;
  }, [users, search, roleFilter, deptFilter, subDeptFilter, departments, activeFilter]);

  const hasFilter = search || roleFilter || deptFilter || subDeptFilter || activeFilter;

  const canSee = canManageUsers(myRole);

  const managersForDropdown = useMemo(() => {
    // Filter who can be a reporting superior based on the selected role
    let allowedRoles;
    if (role === "Director") allowedRoles = ["Admin"];
    else if (role === "CXO") allowedRoles = ["Director"];
    else if (role === "Head") allowedRoles = ["CXO"];
    else if (role === "Manager") allowedRoles = ["Head"];
    else allowedRoles = ["Manager"];
    return users.filter((u) => u.isActive !== false && allowedRoles.includes(u.role));
  }, [users, role]);

  async function loadUsers() {
    setError("");
    setLoading(true);
    try {
      const [usersRes, deptsRes] = await Promise.all([
        apiFetch("/api/users"),
        apiFetch("/api/departments"),
      ]);
      if (!usersRes.ok) throw new Error(await safeReadError(usersRes));
      const data = await usersRes.json();
      setUsers(Array.isArray(data) ? data : []);
      if (deptsRes.ok) {
        const depts = await deptsRes.json();
        setDepartments(Array.isArray(depts) ? depts : []);
      }
    } catch (e) {
      setUsers([]);
      setError(e?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canSee) loadUsers();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSee]);

  const reportsToRequired = role !== "Admin";

  async function createUser() {
    setError("");

    if (!name.trim()) return setError("Name is required");
    if (!email.trim()) return setError("Email is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Enter a valid email address");
    if (!password.trim()) return setError("Password is required");
    if (reportsToRequired && !reportsToUserId)
      return setError("Reporting manager is required for all roles except Admin");

    setSaving(true);
    try {
      const res = await apiFetch("/api/users", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password: password.trim(),
          role,
          reportsToUserId: reportsToUserId || null,
          departmentId: subDeptId || parentDeptId || null,
        }),
      });

      if (!res.ok) {
        const msg = await safeReadError(res);
        throw new Error(msg || "Failed to create user");
      }

      setName("");
      setEmail("");
      setPassword("");
      setRole("Staff");
      setReportsToUserId("");
      setParentDeptId("");
      setSubDeptId("");
      showToast("User created");
      await loadUsers();
    } catch (e) {
      setError(e?.message || "Failed to create user");
    } finally {
      setSaving(false);
    }
  }

  const { totalPages, safePage, paginated } = usePagination(filteredUsers, PAGE_SIZE);
  const currentPage = safePage(page);
  const pagedUsers = paginated(currentPage);

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Users & Roles" subtitle="Admin access required" />

        <div className="glass-card" style={{ padding: 24 }}>
          You don’t have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div>
      <Toast toast={toast} onDismiss={dismissToast} />
      {/* Header */}
      <PageHeader
        title="Users & Roles"
        subtitle="Manage onboarding, roles, and reporting structure"
      />

      <RolePermissionsReference />

      {/* Create User Card */}
      <div className="glass-card" style={{ marginBottom: 24, overflow: "hidden" }}>
        <button
          onClick={() => setCreateOpen((v) => !v)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 24px", background: "none", border: "none", cursor: "pointer", textAlign: "left",
            borderBottom: createOpen ? "1px solid var(--border)" : "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 16 }}>👤</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-1)" }}>Create User</div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 1 }}>Add new team members and define their reporting hierarchy</div>
            </div>
          </div>
          <span style={{ fontSize: 12, color: "var(--text-3)", transform: createOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>▼</span>
        </button>

        {createOpen && <div style={{ padding: 24 }}>

        {error && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid var(--danger-border)",
              background: "var(--danger-soft)",
              color: "var(--danger-text)",
              marginBottom: 18,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
          }}
        >
          <FormField label="Name">
            <input
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rahul Sharma"
            />
          </FormField>

          <FormField label="Email">
            <input
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. rahul@tackl.com"
            />
          </FormField>

          <FormField label="Password">
            <input
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Set a password"
              type="password"
            />
          </FormField>

          <FormField label="Role">
            <select className="form-input" value={role} onChange={(e) => { setRole(e.target.value); setReportsToUserId(""); }}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </FormField>

          <div style={{ gridColumn: "1 / -1" }}>
            <FormField
              label={
                <>
                  Reports To{" "}
                  {reportsToRequired ? (
                    <span style={{ color: "var(--danger)" }}>*</span>
                  ) : (
                    <span style={{ color: "var(--text-2)" }}>(optional)</span>
                  )}
                </>
              }
            >
              <select
                className="form-input"
                value={reportsToUserId}
                onChange={(e) => setReportsToUserId(e.target.value)}
              >
                <option value="">
                  {reportsToRequired
                    ? role === "Director" ? "Select Admin"
                    : role === "CXO" ? "Select Director"
                    : role === "Head" ? "Select CXO"
                    : role === "Manager" ? "Select Head"
                    : "Select Manager"
                    : "None"}
                </option>
                {managersForDropdown.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Department (optional)">
              <select
                className="form-input"
                value={parentDeptId}
                onChange={(e) => { setParentDeptId(e.target.value); setSubDeptId(""); }}
              >
                <option value="">No Department</option>
                {departments.filter(d => !d.parentDepartmentId).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </FormField>
            {parentDeptId && departments.some(d => String(d.parentDepartmentId) === String(parentDeptId)) && (
              <FormField label="Sub-department (optional)">
                <select
                  className="form-input"
                  value={subDeptId}
                  onChange={(e) => setSubDeptId(e.target.value)}
                >
                  <option value="">— None (stays in parent dept)</option>
                  {departments.filter(d => String(d.parentDepartmentId) === String(parentDeptId)).map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </FormField>
            )}
          </div>
        </div>

        <div
          style={{
            marginTop: 22,
            display: "flex",
            gap: 12,
            alignItems: "center",
          }}
        >
          <button
            className="btn"
            onClick={createUser}
            disabled={saving}
            style={{ padding: "10px 16px", borderRadius: 12 }}
          >
            {saving ? (
              <>
                <Loader size="sm" inline style={{ marginRight: 8 }} />
                Creating...
              </>
            ) : (
              "Create User"
            )}
          </button>

          <button
            className="btn-ghost"
            onClick={loadUsers}
            disabled={loading}
            style={{ padding: "10px 16px", borderRadius: 12 }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        </div>}
      </div>

      {/* Filter Bar */}
      <div style={{
        display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
        marginBottom: 14, padding: "10px 14px",
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12,
      }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-3)", pointerEvents: "none" }}>🔍</span>
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search name or email..."
            style={{
              padding: "5px 10px 5px 28px", borderRadius: 20, fontSize: 12, width: 200, outline: "none",
              border: search ? "1.5px solid var(--brand)" : "1px solid var(--border-strong)",
              background: search ? "var(--brand-soft)" : "var(--surface-2)", color: "var(--text-1)",
            }} />
        </div>
        <div style={{ width: 1, height: 18, background: "var(--border-strong)", margin: "0 2px" }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.1em", marginRight: 2 }}>Filter</span>
        <FilterChip value={roleFilter} onChange={(v) => { setRoleFilter(v); setPage(1); }} label="Role"
          options={ROLES.map((r) => ({ value: r, label: r }))} />
        {topLevelDepts.length > 0 && (
          <FilterChip value={deptFilter} onChange={(v) => { setDeptFilter(v); setSubDeptFilter(""); setPage(1); }} label="Department"
            options={topLevelDepts.map((d) => ({ value: d.id, label: d.name }))} />
        )}
        {subDeptsForSelected.length > 0 && (
          <FilterChip value={subDeptFilter} onChange={(v) => { setSubDeptFilter(v); setPage(1); }} label="Sub-dept"
            options={subDeptsForSelected.map((d) => ({ value: d.id, label: d.name }))} />
        )}
        <FilterChip value={activeFilter} onChange={(v) => { setActiveFilter(v); setPage(1); }} label="Status"
          options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
        {hasFilter && (
          <>
            <div style={{ width: 1, height: 18, background: "var(--border-strong)", margin: "0 4px" }} />
            <button onClick={() => { setSearch(""); setRoleFilter(""); setDeptFilter(""); setSubDeptFilter(""); setActiveFilter(""); setPage(1); }}
              style={{ padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "var(--danger-bg)", color: "var(--danger-text)", border: "1px solid #fecdd3", cursor: "pointer" }}>
              ✕ Clear
            </button>
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>{filteredUsers.length} of {users.length} users</span>
          </>
        )}
      </div>

      {/* Users Table */}
      <div className="glass-card" style={{ padding: 0 }}>
        <div className="tasks-table">
          <div
            className="tasks-row header"
            style={{ gridTemplateColumns: "1.4fr 1.8fr 1fr 1fr 1.1fr 1.4fr" }}
          >
            <div>Name</div>
            <div>Email</div>
            <div>Role</div>
            <div>Department</div>
            <div>Reports To</div>
            <div>Actions</div>
          </div>

          {loading && <Loader.TableRows rows={8} />}

          {!loading && filteredUsers.length === 0 && (
            <EmptyState
              icon="👥"
              title={hasFilter ? "No users match your filters" : "No users yet"}
              subtitle={hasFilter ? "Try adjusting or clearing your filters." : "Users will appear here once accounts are created."}
            />
          )}

          {!loading &&
            pagedUsers.map((u) => {
              const manager = users.find((x) => x.id === u.reportsToUserId);
              return (
                <div
                  key={u.id}
                  className="tasks-row"
                  style={{
                    gridTemplateColumns: "1.4fr 1.8fr 1fr 1fr 1.1fr 1.4fr",
                  }}
                >
                  <div className="task-title">{u.name}</div>
                  <div style={{ color: "var(--text-2)" }}>{u.email}</div>
                  <div>
                    <span className={`status-pill status-${u.role}`}>
                      {u.role}
                    </span>
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {u.departmentName ? (
                      u.parentDepartmentName ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <span style={{ color: "var(--text-1)", fontWeight: 500 }}>{u.departmentName}</span>
                          <span style={{ fontSize: 11, color: "var(--text-3)" }}>{u.parentDepartmentName}</span>
                        </div>
                      ) : (
                        <span style={{ color: "var(--text-2)" }}>{u.departmentName}</span>
                      )
                    ) : (
                      <span style={{ color: "var(--text-3)" }}>—</span>
                    )}
                  </div>
                  <div style={{ color: "var(--text-2)" }}>
                    {manager ? manager.name : "—"}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        color: u.isActive === false ? "var(--danger-text)" : "var(--success-text)",
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {u.isActive === false ? "Inactive" : "Active"}
                    </span>

                    <button
                      className="btn-ghost"
                      style={{
                        padding: "4px 9px",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingUser(u);
                      }}
                    >
                      Edit
                    </button>

                    <button
                      className="btn-ghost"
                      style={{
                        padding: "4px 9px",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      disabled={u.id === me?.id}
                      title={u.id === me?.id ? "You can't deactivate yourself" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmStatusUser(u);
                      }}
                    >
                      {u.isActive === false ? "Activate" : "Deactivate"}
                    </button>
                  </div>
                </div>
              );
            })}
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => setPage(safePage(p))}
            total={filteredUsers.length}
            pageSize={PAGE_SIZE}
          />
        </div>
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          users={users}
          departments={departments}
          isAdmin={myRole === "Admin"}
          onClose={() => setEditingUser(null)}
          onSaved={async () => {
            setEditingUser(null);
            showToast("User updated");
            await loadUsers();
          }}
        />
      )}

      {confirmStatusUser && (
        <div className="modal-overlay" onClick={() => setConfirmStatusUser(null)}>
          <div className="glass-card" style={{ width: 400, padding: 28, borderRadius: 18, boxShadow: "0 25px 60px rgba(0,0,0,0.14)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 16, fontWeight: 700 }}>
              {confirmStatusUser.isActive === false ? "Activate User?" : "Deactivate User?"}
            </h3>
            <p style={{ color: "var(--text-2)", fontSize: 13, marginBottom: 22 }}>
              {confirmStatusUser.isActive === false
                ? <>Activate <strong>{confirmStatusUser.name}</strong>? They will regain access to the system.</>
                : <>Deactivate <strong>{confirmStatusUser.name}</strong>? They will lose access to the system immediately.</>}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn-ghost" style={{ padding: "9px 16px", borderRadius: 10 }} onClick={() => setConfirmStatusUser(null)}>
                Cancel
              </button>
              <button
                style={{
                  padding: "9px 16px", borderRadius: 10, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer",
                  background: confirmStatusUser.isActive === false ? "#16a34a" : "#dc2626",
                  color: "white",
                }}
                onClick={async () => {
                  const u = confirmStatusUser;
                  try {
                    const res = await apiFetch(`/api/users/${u.id}/status`, {
                      method: "PUT",
                      body: JSON.stringify({ isActive: u.isActive === false ? true : false }),
                    });
                    if (!res.ok) throw new Error(await safeReadError(res));
                    setConfirmStatusUser(null);
                    showToast(`User ${u.isActive === false ? "activated" : "deactivated"}`);
                    await loadUsers();
                  } catch (err) {
                    setError(err?.message || "Failed to update user status");
                  }
                }}
              >
                {confirmStatusUser.isActive === false ? "Activate" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function FormField({ label, children }) {
  return (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

async function safeReadError(res) {
  try {
    const text = await res.text();
    return text;
  } catch {
    return "";
  }
}
